import { Box } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteAlert from "../../../../components/Alert/DeleteAlert";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import {
  setBlogCurrentPage,
  setBlogPageLimit,
  setBlogTotalCount,
  setBlogTotalPages,
} from "../../../../lib/slices/pagination/blogPageSlice";
import { deleteBlog, getAllBlogs } from "../../../../services/blogsApiFn";
import { handleOpenSnackBar } from "../../../../utils";

const headerData = [
  "blogTitle",
  // "blogCategory",
  "blogTopic",
  "blogSmallDescription",
  "actions",
];

const tableRows = [
  "blog_title",
  // "blog_category",
  "blog_topic",
  "blog_small_description",
  "actions",
];

function BlogList() {
  // Reference to open the delete dialog
  const openDeleteDialog = useRef(null);
  // Reference to store the fetch ID
  const fetchIdRef = useRef(0);
  // Navigation Hook
  const navigate = useNavigate();
  // Dispatch Hook
  const dispatch = useDispatch();
  const { t } = useTranslation(["blog", "common"]);
  // State to store the blogs
  const [allBlogs, setAllBlogs] = useState([]);
  // State to store the loading state
  const [isLoading, setIsLoading] = useState(false);
  // State to store the pagination
  const pagination = useSelector((state) => state.blogPaginationReducer);

  // Function to fetch the blogs
  const fetchAllBlogs = async (page, limit) => {
    const fetchId = ++fetchIdRef.current;
    try {
      setIsLoading(true);
      const response = await getAllBlogs({ page, limit });
      if (fetchId !== fetchIdRef.current) return;
      if (response?.data?.status === "success") {
        const blogs = response?.data?.data?.data || [];
        const paginationData = response?.data?.data?.pagination || {};
        setAllBlogs(blogs);
        dispatch(setBlogPageLimit(paginationData.limit || limit));
        dispatch(setBlogCurrentPage(paginationData.page || page));
        dispatch(
          setBlogTotalCount(
            paginationData.total ?? paginationData.totalBlogs ?? blogs.length,
          ),
        );
        dispatch(setBlogTotalPages(paginationData.totalPages ?? 1));
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      handleOpenSnackBar("error", error?.message);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Effect to fetch the blogs
  useEffect(() => {
    fetchAllBlogs(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  // Function to handle the page change
  const handlePageChange = (event, newPage) => {
    dispatch(setBlogCurrentPage(newPage + 1));
  };

  // Function to handle the rows per page change
  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setBlogPageLimit(limit));
    dispatch(setBlogCurrentPage(1));
  };

  // Function to open the delete dialog

  const handleOpenDeleteDialog = (params) => {
    openDeleteDialog.current.openDialog({
      data: params.row,
      warningText: t("deleteBlogConfirmation"),
    });
  };

  // Function to handle the delete
  const handleDelete = async (params) => {
    try {
      setIsLoading(true);
      const response = await deleteBlog(params.data._id);
      if (response.data.status === "success") {
        openDeleteDialog.current.closeDialog();
        fetchAllBlogs(pagination.page, pagination.limit);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Box>
        <GlobalButton
          btnText={"addNew"}
          handleClickBtn={() => navigate("/blogs/add")}
        />
      </Box>
    );
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allBlogs}
        showActions
        showEdit
        showDelete
        handleEdit={({ row }) => {
          navigate(`/blogs/edit/${row?._id}`);
        }}
        handleDelete={handleOpenDeleteDialog}
        paginatedData={pagination}
        showDynamicPagination={true}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 30, 50, 100]}
        isLoading={isLoading}
      />
    );
  }, [allBlogs, isLoading]);

  return (
    <PaperContainer
      title={t("allBlogs")}
      subtitle={t("blogDescription")}
      headerActions={renderHeaderAction()}
    >
      {renderTable}
      <DeleteAlert ref={openDeleteDialog} handleDelete={handleDelete} />
    </PaperContainer>
  );
}

export default BlogList;
